import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { EmptyState } from '@/components/shared/EmptyState';
import { Button } from '@/components/ui/button';
import { useLabel } from '@/hooks/useLabel';

type ChartErrorBoundaryProps = {
  children: ReactNode;
  fallback?: ReactNode;
  title: string;
  description: string;
  retryLabel: string;
};

type ChartErrorBoundaryState = {
  hasError: boolean;
  error?: Error;
};

/** Collapsible stack trace of a chart error (dev builds only). */
function StackTraceDetails({ stack }: { stack: string }) {
  const label = useLabel('analytics.chartError.stackTrace', 'Stack trace');
  return (
    <details className="w-full max-w-sm text-left">
      <summary className="flex cursor-pointer items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
        <ChevronDown className="h-3 w-3" aria-hidden />
        <span className="sr-only">{label}</span>
      </summary>
      <pre className="mt-1 max-h-24 overflow-auto whitespace-pre-wrap rounded bg-muted p-2 text-xs font-mono text-muted-foreground">
        {stack}
      </pre>
    </details>
  );
}

export class ChartErrorBoundary extends Component<
  ChartErrorBoundaryProps,
  ChartErrorBoundaryState
> {
  constructor(props: ChartErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ChartErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Chart rendering error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      const { title, description, retryLabel } = this.props;

      return (
        <EmptyState
          tone="error"
          size="compact"
          icon={AlertTriangle}
          title={title}
          description={this.state.error?.message ?? description}
          className="min-h-[200px]"
          action={
            <div className="flex w-full flex-col items-center gap-2">
              {import.meta.env.DEV && this.state.error?.stack && (
                <StackTraceDetails stack={this.state.error.stack} />
              )}
              <Button
                variant="outline"
                size="sm"
                className="mt-1"
                onClick={() => this.setState({ hasError: false, error: undefined })}
              >
                {retryLabel}
              </Button>
            </div>
          }
        />
      );
    }

    return this.props.children;
  }
}
